// Deterministic parser for chapter notes outputs. Handles both the legacy
// bold-label format (**Overview**: ...) and the newer heading format
// (#### Overview), including the roast framework's alternate labels.
// Falls back to ok:false (render as plain markdown) when nothing parses.

export type NoteBlockType =
  | 'overview'
  | 'strength'
  | 'critical'
  | 'important'
  | 'priorities'
  | 'preservation'
  | 'connections'
  | 'other';

export interface NoteBlock {
  type: NoteBlockType;
  label: string;
  markdown: string;
  itemCount: number;
  hasContent: boolean;
}

export interface ChapterNote {
  id: string;
  number: number;
  title: string;
  heading: string;
  blocks: NoteBlock[];
  severity: { critical: number; important: number; strength: number };
  complete: boolean;
}

export interface ParsedChapterNotes {
  intro: string;
  chapters: ChapterNote[];
  outro: string;
  ok: boolean;
}

// Ordered label → type table. First match wins; covers standard and roast wording.
const LABEL_TYPES: Array<[RegExp, NoteBlockType]> = [
  [/overview|chapter summary/i, 'overview'],
  [/strength|actually working/i, 'strength'],
  [/critical areas|roast-worthy/i, 'critical'],
  [/enhancement opportunit/i, 'important'],
  [/revision priorities|roast prescription/i, 'priorities'],
  [/preservation|don'?t you dare touch/i, 'preservation'],
  [/connections|ripple effects/i, 'connections'],
];

const CHAPTER_HEADING = /^#{2,4}\s+Chapter\s+(\d+)\b[:.\s–—-]*(.*?)\s*$/;
// A block label is either a heading (#### Label) or a bold-only lead
// (**Label**: optional inline content). Bullets ("*   **Bold**: ...") don't
// match because of the line-start anchor.
const HEADING_LABEL = /^#{3,5}\s+(.+?):?\s*$/;
const BOLD_LABEL = /^\*\*([^*\n]+?)\*\*:?\s*(.*)$/;

function labelType(label: string): NoteBlockType | null {
  for (const [re, type] of LABEL_TYPES) {
    if (re.test(label)) return type;
  }
  return null;
}

function countListItems(markdown: string): number {
  return (markdown.match(/^\s*(?:[*+-]|\d+\.)\s+/gm) ?? []).length;
}

function blockHasContent(markdown: string, itemCount: number): boolean {
  const text = markdown.trim();
  if (!text) return false;
  if (/^\W*none\s+(identified|noted)/i.test(text)) return false;
  return itemCount > 0 || text.length > 0;
}

function makeBlock(type: NoteBlockType, label: string, markdown: string): NoteBlock {
  const itemCount = countListItems(markdown);
  return {
    type,
    label: label.replace(/:$/, '').trim(),
    markdown: markdown.trim(),
    itemCount,
    hasContent: blockHasContent(markdown, itemCount),
  };
}

function parseChapterBody(body: string): NoteBlock[] {
  const lines = body.split('\n');
  const blocks: NoteBlock[] = [];
  let currentType: NoteBlockType = 'other';
  let currentLabel = '';
  let buffer: string[] = [];

  const flush = () => {
    const markdown = buffer.join('\n').trim();
    if (markdown || currentLabel) {
      blocks.push(makeBlock(currentType, currentLabel, markdown));
    }
    buffer = [];
  };

  for (const line of lines) {
    let matchedLabel: string | null = null;
    let inlineRest = '';

    const headingMatch = line.match(HEADING_LABEL);
    const boldMatch = line.match(BOLD_LABEL);
    if (headingMatch && labelType(headingMatch[1]!)) {
      matchedLabel = headingMatch[1]!;
    } else if (boldMatch && labelType(boldMatch[1]!)) {
      matchedLabel = boldMatch[1]!;
      inlineRest = boldMatch[2] ?? '';
    }

    if (matchedLabel) {
      flush();
      currentType = labelType(matchedLabel)!;
      currentLabel = matchedLabel;
      if (inlineRest.trim()) buffer.push(inlineRest.trim());
    } else {
      buffer.push(line);
    }
  }
  flush();

  return blocks.filter(b => b.markdown || b.label);
}

export function parseChapterNotes(markdown: string): ParsedChapterNotes {
  const result: ParsedChapterNotes = { intro: '', chapters: [], outro: '', ok: false };
  if (!markdown?.trim()) return result;

  const lines = markdown.split('\n');

  // Locate chapter headings and the outro boundary (first non-chapter heading
  // at the same or higher level after the chapters begin).
  interface Marker {
    line: number;
    number: number;
    title: string;
    heading: string;
  }
  const markers: Marker[] = [];
  let outroStart = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const chapterMatch = line.match(CHAPTER_HEADING);
    if (chapterMatch) {
      if (outroStart !== -1) outroStart = -1; // chapters resumed; not an outro after all
      markers.push({
        line: i,
        number: parseInt(chapterMatch[1]!, 10),
        title: chapterMatch[2]?.trim() ?? '',
        heading: line.replace(/^#+\s*/, '').trim(),
      });
    } else if (markers.length > 0 && outroStart === -1 && /^#{2,4}\s+\S/.test(line)) {
      // A heading that is a known block label (e.g. "#### Overview") belongs
      // to the current chapter, not the outro.
      const headingText = line.match(HEADING_LABEL)?.[1];
      if (!headingText || !labelType(headingText)) {
        outroStart = i;
      }
    }
  }

  if (markers.length === 0) return result;

  result.intro = lines
    .slice(0, markers[0]!.line)
    .join('\n')
    .replace(/\n-{3,}\s*$/g, '')
    .trim();

  const endOfChapters = outroStart === -1 ? lines.length : outroStart;
  if (outroStart !== -1) {
    result.outro = lines.slice(outroStart).join('\n').trim();
  }

  for (let m = 0; m < markers.length; m++) {
    const marker = markers[m]!;
    const bodyEnd = m + 1 < markers.length ? markers[m + 1]!.line : endOfChapters;
    const body = lines
      .slice(marker.line + 1, bodyEnd)
      .join('\n')
      .replace(/\n-{3,}\s*$/g, '')
      .trim();

    const blocks = parseChapterBody(body);
    const countFor = (type: NoteBlockType) =>
      blocks.filter(b => b.type === type && b.hasContent).reduce((sum, b) => sum + Math.max(b.itemCount, 1), 0);

    // The document is complete for this chapter if another chapter or the
    // outro follows it; the final chapter of a streaming doc is provisional.
    const isLast = m === markers.length - 1;
    result.chapters.push({
      // Position, not chapter number: models sometimes repeat a chapter
      id: `chapter-note-${m + 1}`,
      number: marker.number,
      title: marker.title,
      heading: marker.heading,
      blocks,
      severity: {
        critical: countFor('critical'),
        important: countFor('important'),
        strength: countFor('strength'),
      },
      complete: !isLast || outroStart !== -1,
    });
  }

  result.ok = true;
  return result;
}
